import { Children, FC, Fragment, ReactNode, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import SupportAgentOutlined from '@mui/icons-material/SupportAgentOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import PlaylistAddCheckOutlined from '@mui/icons-material/PlaylistAddCheckOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import ArrowOutward from '@mui/icons-material/ArrowOutward';
import Close from '@mui/icons-material/Close';
import McpLogoIcon from 'src/components/McpLogoIcon';
import UserContext from 'src/contexts/UserContext';

export const SUPPORT_EMAIL = 'support@reinvestwealth.com';
const CPA_MEETING_URL =
  'https://meetings.hubspot.com/reinvestwealth/meet-with-an-accountant';
// Public help desk (FAQ + feature guides). Localized per market; the
// locale-less URL redirects to en-ca, so US businesses get en-us explicitly.
export const helpDeskUrl = (companyLocationHQ?: string): string =>
  `https://www.reinvestwealth.com/${
    companyLocationHQ?.startsWith('US') ? 'en-us' : 'en-ca'
  }/helpdesk`;
// The "AI / MCP" tab on the profile page, where users connect Claude/ChatGPT.
export const MCP_SETTINGS_PATH = '/app/profile?tab=7';

// Any part of the app can open the support modal (which is mounted once, in
// the TopBar) by dispatching this event. Avoids a second modal instance and a
// second copy of the onboarding-state fetches.
export const OPEN_SUPPORT_MODAL_EVENT = 'openSupportModal';
export const openSupportModal = (): void => {
  window.dispatchEvent(new CustomEvent(OPEN_SUPPORT_MODAL_EVENT));
};

// Preset question that is auto-asked to Luca when a new user picks
// "Migrating from another platform?" in the support modal.
export const ONBOARDING_SUPPORT_QUESTION =
  "I'm migrating to ReInvestWealth from another accounting platform (such as QuickBooks, Xero, Wave, FreshBooks, or a spreadsheet) and I need help with the migration. What are my options for moving my historical transactions, prior-year books, chart of accounts, and reports over to ReInvestWealth, and what are the step-by-step next actions I should take?";

interface SupportOptionProps {
  icon: ReactNode;
  title: string;
  description: string;
  // Short trailing detail (e.g. a price). Rendered as quiet text, not a chip.
  meta?: string;
  to?: string;
  href?: string;
  onClick?: () => void;
}

const SupportOption: FC<SupportOptionProps> = ({
  icon,
  title,
  description,
  meta,
  to,
  href,
  onClick,
}) => {
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const isExternal = !to && !!href;

  const linkProps = to
    ? { component: RouterLink, to }
    : href
      ? { component: 'a', href, target: '_blank', rel: 'noopener noreferrer' }
      : {};

  const Arrow = isExternal ? ArrowOutward : ChevronRight;

  return (
    <ButtonBase
      {...(linkProps as any)}
      onClick={onClick}
      focusRipple
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        gap: 2,
        px: 1.5,
        py: 1.75,
        borderRadius: 2.5,
        transition: 'background-color .15s ease',
        '&:hover, &.Mui-focusVisible': {
          backgroundColor: alpha(accent, 0.06),
        },
        '&:hover .support-option-icon, &.Mui-focusVisible .support-option-icon':
          {
            backgroundColor: accent,
            color: '#fff',
          },
        '&:hover .support-option-arrow, &.Mui-focusVisible .support-option-arrow':
          {
            color: accent,
            transform: isExternal ? 'translate(1px, -1px)' : 'translateX(2px)',
          },
      }}
    >
      <Box
        className="support-option-icon"
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(accent, 0.1),
          color: accent,
          transition: 'background-color .15s ease, color .15s ease',
          '& svg': { fontSize: 21, width: 21, height: 21 },
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontWeight: 600,
            fontSize: 15,
            lineHeight: 1.3,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        <Typography
          component="span"
          sx={{
            display: 'block',
            mt: 0.25,
            fontSize: 13.5,
            lineHeight: 1.5,
            color: 'text.secondary',
          }}
        >
          {description}
          {meta && (
            <Box
              component="span"
              sx={{
                display: { xs: 'inline', sm: 'none' },
                whiteSpace: 'nowrap',
              }}
            >
              {' '}
              {meta}
            </Box>
          )}
        </Typography>
      </Box>

      {meta && (
        <Typography
          component="span"
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {meta}
        </Typography>
      )}

      <Arrow
        className="support-option-arrow"
        sx={{
          fontSize: 20,
          color: 'text.disabled',
          flexShrink: 0,
          transition: 'transform .15s ease, color .15s ease',
        }}
      />
    </ButtonBase>
  );
};

// Rows bleed slightly past the text column so the hover fill has breathing
// room while titles stay aligned with the heading. Hairline dividers sit
// between rows, inset back to the text column so they line up with the footer.
const OptionList: FC<{ children: ReactNode }> = ({ children }) => {
  const items = Children.toArray(children);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mx: -1.5 }}>
      {items.map((child, i) => (
        <Fragment key={i}>
          {i > 0 && <Divider sx={{ mx: 1.5 }} />}
          {child}
        </Fragment>
      ))}
    </Box>
  );
};

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
  // Shown during the first weeks after the business is created.
  showOnboardingSupport?: boolean;
  // Shown when the dashboard onboarding checklist has been dismissed.
  showOnboardingGuide?: boolean;
  onReopenOnboarding?: () => void;
}

const SupportModal: FC<SupportModalProps> = ({
  open,
  onClose,
  showOnboardingSupport = false,
  showOnboardingGuide = false,
  onReopenOnboarding,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { userData } = useContext(UserContext);

  const meetingParams = new URLSearchParams();
  if (userData?.companyEmail) meetingParams.set('email', userData.companyEmail);
  if (userData?.primaryContactFirstName) {
    meetingParams.set('firstname', userData.primaryContactFirstName);
  }
  if (userData?.primaryContactLastName) {
    meetingParams.set('lastname', userData.primaryContactLastName);
  }
  const meetingQuery = meetingParams.toString();
  const meetingUrl = meetingQuery
    ? `${CPA_MEETING_URL}?${meetingQuery}`
    : CPA_MEETING_URL;

  const helpDeskHref = helpDeskUrl(userData?.companyLocationHQ);

  const onboardingSupportUrl = `/app/luca?presetQuestion=${encodeURIComponent(
    ONBOARDING_SUPPORT_QUESTION
  )}&t=${Date.now()}`;

  const hasGettingStarted = showOnboardingSupport || showOnboardingGuide;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby="support-modal-title"
      PaperProps={{
        sx: {
          maxWidth: 540,
          borderRadius: '20px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          backgroundImage: 'none',
          // On phones, present as a bottom sheet rather than a floating card.
          ...(isMobile && {
            position: 'fixed',
            bottom: 0,
            m: 0,
            width: '100%',
            maxWidth: '100%',
            maxHeight: '92vh',
            borderRadius: '20px 20px 0 0',
          }),
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 4 },
          pt: { xs: 2.5, sm: 3.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Typography
          id="support-modal-title"
          component="h2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 21, sm: 22 },
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
            color: 'text.primary',
          }}
        >
          How can we help?
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{
            mr: -1,
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 4 },
          pt: '20px !important',
          pb: { xs: 2.5, sm: 3.5 },
        }}
      >
        <OptionList>
          <SupportOption
            icon={<AutoAwesomeOutlined />}
            title="Ask Luca"
            description="Instant answers about your books and our support docs."
            to="/app/luca"
            onClick={onClose}
          />
          <SupportOption
            icon={<SupportAgentOutlined />}
            title="Message our support team"
            description="Report bugs, feature requests, or other technical issues."
            to="/app/account/ticket"
            onClick={onClose}
          />
          <SupportOption
            icon={<EventAvailableOutlined />}
            title="Book a call with a CPA"
            description="For tax, year-end, or business questions."
            meta="$49 · 30 min"
            href={meetingUrl}
            onClick={onClose}
          />
          <SupportOption
            icon={<McpLogoIcon />}
            title="Connect Claude or ChatGPT"
            description="Work on your books from the AI you already use."
            to={MCP_SETTINGS_PATH}
            onClick={onClose}
          />
        </OptionList>

        {hasGettingStarted && (
          <>
            <Divider />
            <Typography
              component="p"
              sx={{
                mt: 2.5,
                mb: 0.5,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Getting started
            </Typography>
            <OptionList>
              {showOnboardingSupport && (
                <SupportOption
                  icon={<SwapHorizOutlined />}
                  title="Migrating from another platform?"
                  description="Get a step-by-step plan to bring your books over."
                  to={onboardingSupportUrl}
                  onClick={onClose}
                />
              )}
              {showOnboardingGuide && (
                <SupportOption
                  icon={<PlaylistAddCheckOutlined />}
                  title="Reopen your setup checklist"
                  description="Bring the onboarding guide back to your dashboard."
                  onClick={onReopenOnboarding}
                />
              )}
            </OptionList>
          </>
        )}

        <Typography
          component="p"
          sx={{
            mt: 3,
            pt: 2.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
            fontSize: 13.5,
            color: 'text.secondary',
          }}
        >
          Prefer email?{' '}
          <Link
            href={`mailto:${SUPPORT_EMAIL}`}
            color="secondary"
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {SUPPORT_EMAIL}
          </Link>
          <Box component="span" sx={{ mx: 1.25, color: 'text.disabled' }}>
            ·
          </Box>
          <Link
            href={helpDeskHref}
            target="_blank"
            rel="noopener noreferrer"
            color="secondary"
            underline="hover"
            sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Help Desk
          </Link>
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default SupportModal;
